import { useTranslation } from 'react-i18next';

import type { JOB_ROLE_SURFACE_KEYS } from '@palaccount/contracts';

/**
 * BR-HR-01-16 — "Any screen that asks for a person must state which list it
 * draws from. A task assignee comes from user accounts; a payslip comes from
 * employees."
 *
 * The rule exists because this build genuinely has TWO people lists that do
 * not imply each other (BR-HR-01-15): `useEmployeePicker` and the surface
 * dropdowns read EMPLOYEES, while a task assignee reads USER ACCOUNTS — and
 * before this line nothing on any screen said which one a control was
 * offering. The same person can be in one list and not the other, so a picker
 * that "doesn't have" somebody is not broken; it is drawing from the other
 * list, and the screen should say so.
 *
 * One component rather than four ad-hoc lines so the wording cannot drift
 * per screen, and so the next person-asking control has an obvious thing to
 * reach for. The surface variant names the surface's own label
 * (`hr.surface.*` — the labels ADM screens already use) rather than a seeded
 * role name, because tenant-made roles can feed a surface too
 * (BR-HR-01-05d) and naming one role would be wrong the day a tenant adds a
 * second.
 */
export function PersonSourceHint(
  props:
    | { source: 'accounts' }
    | { source: 'employees'; surface?: (typeof JOB_ROLE_SURFACE_KEYS)[number] },
) {
  const { t } = useTranslation();
  const text =
    props.source === 'accounts'
      ? t('hr.pickerSourceAccounts')
      : props.surface !== undefined
        ? t('hr.pickerSourceEmployeesSurface', { surface: t(`hr.surface.${props.surface}`) })
        : t('hr.pickerSourceEmployees');
  return (
    <p data-testid="person-source-hint" className="text-xs text-muted-foreground">
      {text}
    </p>
  );
}
